"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { Menubar as MB } from "radix-ui";
import { Command } from "cmdk";
import { Bell, Check, ChevronDown, LogOut, Monitor, Moon, Plus, Repeat, Search, Settings2, ShieldCheck, Sun, UserRound } from "lucide-react";
import type { Empresa, Notificacao } from "@/types";
import { useQueryClient } from "@tanstack/react-query";
import { useSession, useEmpresa, can } from "@/core/session";
import { openScreen } from "@/core/tabs";
import { usePrefs, type ThemePref } from "@/core/prefs";
import { SCREEN_META, type ScreenKey } from "@/core/screen-meta";
import { api } from "@/lib/api";
import { useApiQuery, useInvalidate } from "@/lib/query";
import { fmt } from "@/lib/format";
import { cn } from "@/lib/utils";
import { EnvBadge } from "@/components/status";
import { Kbd } from "@/components/ui/controls";
import { Button } from "@/components/ui/button";
import { menuContentClass, menuItemClass, menuSeparatorClass, Shortcut } from "@/components/ui/menu";
import { Popover, PopoverContent, PopoverTrigger, Tip } from "@/components/ui/overlay";
import { TOM_DOT } from "@/lib/status";
import { MENUS } from "./menus";
import { MENU_ICONS } from "./menu-icons";
import { useUi } from "./ui-store";
import { NovaEmpresaDialog } from "@/features/empresas/nova-empresa-dialog";
import { LogoMarca } from "@/components/logo";

export function AppMenubar() {
  const papel = useSession((s) => s.usuario?.papel);
  // Conta sem plano: só Sistema › Assinatura fica disponível
  const bloqueada = useSession((s) => s.conta?.acesso === "BLOQUEADO");
  const setEncerrar = useUi((s) => s.setEncerrar);
  const rootRef = React.useRef<HTMLDivElement>(null);

  /* Alt sozinho foca o primeiro menu */
  React.useEffect(() => {
    let altAlone = false;
    const down = (e: KeyboardEvent) => {
      altAlone = e.key === "Alt" && !e.ctrlKey && !e.shiftKey && !e.metaKey;
      if (e.key !== "Alt") altAlone = false;
    };
    const up = (e: KeyboardEvent) => {
      if (e.key === "Alt" && altAlone) {
        e.preventDefault();
        rootRef.current?.querySelector<HTMLButtonElement>("[data-menubar-trigger]")?.focus();
      }
      altAlone = false;
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);

  return (
    <header data-print-hide className="flex h-14 shrink-0 items-center gap-1 bg-menubar px-2 text-menubar-fg">
      <span className="inline-flex size-7 items-center justify-center text-menubar-fg" aria-label="Rodix">
        <LogoMarca className="size-5" />
      </span>
      <CompanySwitcher />
      <div className="mx-1 h-5 w-px bg-menubar-hover" />
      <MB.Root ref={rootRef} className="flex h-full items-center gap-1.5" loop>
        {MENUS.map((m) => {
          const Icon = MENU_ICONS[m.id as keyof typeof MENU_ICONS];
          return (
          <MB.Menu key={m.id}>
            {/* Bloco plano: só ícone e rótulo; aberto vira bloco azul sólido */}
            <MB.Trigger
              data-menubar-trigger
              className={cn(
                "group relative flex h-[46px] w-[76px] cursor-pointer flex-col items-center justify-center gap-1 rounded-md text-menubar-fg outline-none select-none",
                "transition-[background-color,color] duration-100 hover:bg-menubar-hover focus-visible:bg-menubar-hover focus-visible:ring-1 focus-visible:ring-primary",
                "data-[state=open]:bg-primary data-[state=open]:text-primary-fg",
              )}
            >
              <Icon className="size-[22px] opacity-90 group-hover:opacity-100 group-data-[state=open]:opacity-100" />
              <span className="text-2xs leading-none font-medium">{m.label}</span>
            </MB.Trigger>
            <MB.Portal>
              <MB.Content className={cn(menuContentClass, "min-w-60")} align="start" sideOffset={6}>
                {m.items.map((it) => {
                  const meta = it.screen;
                  const disabled = bloqueada ? meta !== "assinatura" : meta ? !allowed(meta, papel) : false;
                  return (
                    <React.Fragment key={it.label}>
                      {it.separatorBefore ? <MB.Separator className={menuSeparatorClass} /> : null}
                      <MB.Item
                        disabled={disabled}
                        className={menuItemClass}
                        onSelect={() => {
                          if (it.action === "encerrar-viagem") setEncerrar(true);
                          else if (it.screen) openScreen(it.screen, it.params);
                        }}
                      >
                        <it.icon />
                        {it.label}
                        {it.shortcut ? <Shortcut>{it.shortcut}</Shortcut> : null}
                      </MB.Item>
                    </React.Fragment>
                  );
                })}
              </MB.Content>
            </MB.Portal>
          </MB.Menu>
          );
        })}
      </MB.Root>

      <div className="ml-auto flex items-center gap-1">
        {bloqueada ? null : (
        <button
          type="button"
          onClick={() => useUi.getState().setPalette(true)}
          className="mr-1 flex h-7 w-56 items-center gap-2 rounded-md border border-menubar-hover bg-menubar-hover px-2 text-sm text-menubar-muted outline-none hover:text-menubar-fg focus-visible:ring-1 focus-visible:ring-focus"
        >
          <Search className="size-4" />
          Buscar
          <Kbd className="ml-auto border-menubar-muted/40 bg-transparent text-menubar-muted">Ctrl K</Kbd>
        </button>
        )}
        {bloqueada ? null : <Notifications />}
        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  );
}

function allowed(screen: ScreenKey, papel: Parameters<typeof can>[0]) {
  const acao = SCREEN_META[screen].acao;
  return !acao || can(papel, acao);
}

/* ---------- Empresa ---------- */

/** Logotipo da empresa (enviado em Sistema › Empresa), direto, sem moldura; sem logo, a inicial do nome. */
function LogoEmpresa({ empresa, className }: { empresa: Pick<Empresa, "nomeFantasia" | "logo">; className?: string }) {
  if (empresa.logo) {
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={empresa.logo} alt="" className={cn("shrink-0 object-contain", className)} aria-hidden />;
  }
  return (
    <span className={cn("inline-flex aspect-square shrink-0 items-center justify-center rounded-sm bg-white/15 text-[10px] font-bold leading-none", className)} aria-hidden>
      {empresa.nomeFantasia.trim().charAt(0).toUpperCase()}
    </span>
  );
}

function CompanySwitcher() {
  const empresa = useEmpresa();
  const empresas = useSession((s) => s.empresas);
  const gestor = useSession((s) => s.conta?.papelConta === "DONO" || s.conta?.papelConta === "ADMIN");
  const [open, setOpen] = React.useState(false);
  const [nova, setNova] = React.useState(false);
  const qc = useQueryClient();
  if (!empresa) return null;
  const trocar = (id: string) => {
    if (id !== empresa.id) {
      useSession.getState().selectEmpresa(id);
      qc.removeQueries();
    }
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-7 max-w-56 items-center gap-1.5 rounded-md px-2 text-sm font-semibold tracking-wide text-menubar-fg uppercase outline-none hover:bg-menubar-hover focus-visible:bg-menubar-hover data-[state=open]:bg-menubar-hover"
        >
          <LogoEmpresa empresa={empresa} className="h-5 w-auto max-w-16" />
          <span className="truncate">{empresa.nomeFantasia}</span>
          <ChevronDown className="size-3.5 shrink-0 text-menubar-muted" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-88 p-0" sideOffset={6}>
        <Command loop>
          <div className="flex items-center gap-2 border-b border-border px-2.5">
            <Search className="size-4 text-subtle" />
            <Command.Input autoFocus placeholder="Nome ou CNPJ" className="h-9 flex-1 bg-transparent text-sm outline-none" />
          </div>
          <Command.List className="max-h-72 overflow-auto p-1">
            <Command.Empty className="px-2 py-3 text-center text-xs text-muted">Nenhuma empresa</Command.Empty>
            {empresas.map((e) => (
              <Command.Item
                key={e.id}
                value={`${e.nomeFantasia} ${e.razaoSocial} ${e.cnpj} ${fmt.cnpj(e.cnpj)}`}
                onSelect={() => trocar(e.id)}
                className="flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 data-[selected=true]:bg-primary-soft"
              >
                <LogoEmpresa empresa={e} className="h-7 w-auto max-w-20 text-foreground" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm">{e.nomeFantasia}</span>
                  <span className="block truncate text-xs text-muted tnum">{fmt.cnpj(e.cnpj)}</span>
                </span>
                {e.ambiente === "homologacao" ? <EnvBadge ambiente={e.ambiente} /> : null}
                <Check className={cn("size-3.5 text-primary", e.id === empresa.id ? "opacity-100" : "opacity-0")} />
              </Command.Item>
            ))}
          </Command.List>
        </Command>
        {gestor ? (
          <div className="border-t border-border p-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setNova(true);
              }}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-primary outline-none hover:bg-primary-soft focus-visible:bg-primary-soft"
            >
              <Plus className="size-3.5" />
              Adicionar empresa
            </button>
          </div>
        ) : null}
      </PopoverContent>
      <NovaEmpresaDialog open={nova} onOpenChange={setNova} />
    </Popover>
  );
}

/* ---------- Notificações ---------- */

function Notifications() {
  const { data } = useApiQuery<Notificacao[]>("notificacoes", "/notificacoes", { refetchInterval: 20_000 });
  const invalidate = useInvalidate();
  const [open, setOpen] = React.useState(false);
  const naoLidas = (data ?? []).filter((n) => !n.lida).length;
  const abrir = async (n: Notificacao) => {
    setOpen(false);
    if (!n.lida) {
      await api(`/notificacoes/${n.id}/lida`, { method: "POST" }).catch(() => null);
      invalidate("notificacoes");
    }
    if (n.alvo) openScreen(n.alvo.screen as ScreenKey, n.alvo.params);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tip content="Notificações">
        <PopoverTrigger asChild>
          <button type="button" aria-label={`Notificações: ${naoLidas} não lidas`} className="relative inline-flex size-7 items-center justify-center rounded-md text-menubar-fg outline-none hover:bg-menubar-hover focus-visible:bg-menubar-hover">
            <Bell className="size-4" />
            {naoLidas ? (
              <span className="absolute -top-0.5 -right-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-2xs font-semibold text-primary-fg tnum">{naoLidas}</span>
            ) : null}
          </button>
        </PopoverTrigger>
      </Tip>
      <PopoverContent align="end" className="w-96 p-0" sideOffset={6}>
        <div className="flex h-9 items-center justify-between border-b border-border px-3">
          <span className="text-sm font-semibold">Notificações</span>
          {naoLidas ? (
            <Button
              variant="link"
              size="sm"
              onClick={async () => {
                await api("/notificacoes/lidas", { method: "POST" }).catch(() => null);
                invalidate("notificacoes");
              }}
            >
              Marcar todas
            </Button>
          ) : null}
        </div>
        <ul className="max-h-96 overflow-auto py-1">
          {(data ?? []).slice(0, 8).map((n) => (
            <li key={n.id}>
              <button type="button" onClick={() => abrir(n)} className="flex w-full items-center gap-2.5 px-3 py-2 text-left outline-none hover:bg-row-hover focus-visible:bg-row-hover">
                <span className={cn("size-2 shrink-0 rounded-full", TOM_DOT[n.tom])} />
                <span className={cn("min-w-0 flex-1 truncate text-sm", !n.lida && "font-medium")}>{n.titulo}</span>
                <span className="shrink-0 text-xs text-subtle tnum">{fmt.dayShort(n.data)}</span>
              </button>
            </li>
          ))}
          {data && !data.length ? <li className="px-3 py-4 text-center text-xs text-muted">Nenhuma notificação</li> : null}
        </ul>
        <div className="border-t border-border p-1">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              openScreen("notificacoes");
            }}
            className="h-7 w-full rounded-sm text-sm text-primary hover:bg-primary-soft"
          >
            Ver todas
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* ---------- Tema ---------- */

function ThemeToggle() {
  const theme = usePrefs((s) => s.theme);
  const setTheme = usePrefs((s) => s.setTheme);
  const next: Record<ThemePref, ThemePref> = { system: "light", light: "dark", dark: "system" };
  const label: Record<ThemePref, string> = { system: "Tema: sistema", light: "Tema: claro", dark: "Tema: escuro" };
  const Icon = theme === "light" ? Sun : theme === "dark" ? Moon : Monitor;
  return (
    <Tip content={label[theme]}>
      <button type="button" aria-label={label[theme]} onClick={() => setTheme(next[theme])} className="inline-flex size-7 items-center justify-center rounded-md text-menubar-fg outline-none hover:bg-menubar-hover focus-visible:bg-menubar-hover">
        <Icon className="size-4" />
      </button>
    </Tip>
  );
}

/* ---------- Usuário ---------- */

function UserMenu() {
  const router = useRouter();
  const usuario = useSession((s) => s.usuario);
  const empresas = useSession((s) => s.empresas);
  const iniciais = (usuario?.nome ?? "?")
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const sair = async () => {
    await api("/auth/logout", { method: "POST" }).catch(() => null);
    useSession.getState().logout();
    router.replace("/login");
  };
  return (
    <MB.Root className="flex">
      <MB.Menu>
        <MB.Trigger className="ml-1 inline-flex h-7 items-center gap-1 rounded-md pr-1 pl-0.5 outline-none hover:bg-menubar-hover focus-visible:bg-menubar-hover data-[state=open]:bg-menubar-hover">
          <span className="inline-flex size-6 items-center justify-center rounded-full bg-menubar-hover text-2xs font-semibold text-menubar-fg">{iniciais}</span>
          <ChevronDown className="size-3.5 text-menubar-muted" />
        </MB.Trigger>
        <MB.Portal>
          <MB.Content className={cn(menuContentClass, "min-w-56")} align="end" sideOffset={6}>
            <div className="px-2 py-1.5">
              <div className="truncate text-sm font-medium">{usuario?.nome}</div>
              <div className="truncate text-xs text-muted">{usuario?.email}</div>
            </div>
            <MB.Separator className={menuSeparatorClass} />
            <MB.Item className={menuItemClass} onSelect={() => openScreen("perfil")}>
              <UserRound />
              Perfil
            </MB.Item>
            <MB.Item className={menuItemClass} onSelect={() => openScreen("preferencias")}>
              <Settings2 />
              Preferências
            </MB.Item>
            <MB.Item className={menuItemClass} disabled={empresas.length < 2} onSelect={() => router.push("/empresa")}>
              <Repeat />
              Trocar empresa
            </MB.Item>
            {usuario?.superadmin ? (
              <>
                <MB.Separator className={menuSeparatorClass} />
                <MB.Item className={menuItemClass} onSelect={() => router.push("/admin")}>
                  <ShieldCheck />
                  Administração do SaaS
                </MB.Item>
              </>
            ) : null}
            <MB.Separator className={menuSeparatorClass} />
            <MB.Item className={menuItemClass} onSelect={sair}>
              <LogOut />
              Sair
            </MB.Item>
          </MB.Content>
        </MB.Portal>
      </MB.Menu>
    </MB.Root>
  );
}
